import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, MapPin, Globe, TrendingUp, Users, Star } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';
import { generateHreflangLinks } from '@/lib/swissHreflang';

const ItalianSwissCityServicePage = () => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const service = pathParts[2]; // e.g., 'webdesign', 'seo', 'video-ai'
  const city = pathParts[3] || 'lugano'; // Default to Lugano

  // Italian-Swiss content
  const cityContent: { [key: string]: any } = {
    lugano: {
      name: 'Lugano',
      region: 'Ticino',
      description: {
        it: 'Il centro economico del Ticino con un mix unico di lifestyle mediterraneo e business internazionale.',
        de: 'Das Wirtschaftszentrum des Tessins mit einer einzigartigen Mischung aus mediterranem Lifestyle und internationalem Business.',
        en: 'The economic center of Ticino with a unique mix of Mediterranean lifestyle and international business.',
        fr: 'Le centre économique du Tessin avec un mélange unique de lifestyle méditerranéen et de business international.'
      },
      industries: ['Finanza', 'Turismo', 'Lusso', 'Immobiliare'],
      population: '63,000',
      localProof: {
        it: 'Oltre 150 aziende ticinesi aumentano la loro visibilità online',
        de: 'Über 150 Tessiner Unternehmen steigern ihre Online-Sichtbarkeit',
        en: 'Over 150 Ticino companies increase their online visibility',
        fr: 'Plus de 150 entreprises tessinoises augmentent leur visibilité en ligne'
      },
      specialOffer: {
        it: 'Strategia digitale personalizzata per il mercato ticinese',
        de: 'Personalisierte Digitalstrategie für den Tessiner Markt',
        en: 'Personalized digital strategy for the Ticino market',
        fr: 'Stratégie digitale personnalisée pour le marché tessinois'
      }
    }
  };

  const serviceContent: { [key: string]: any } = {
    'video-ai': {
      name: { it: 'Video AI', de: 'AI-Video', en: 'AI Video', fr: 'Vidéo IA' },
      description: {
        it: 'Video con intelligenza artificiale per comunicare con eleganza e professionalità nel mercato ticinese.',
        de: 'KI-Videos für elegante und professionelle Kommunikation im Tessiner Markt.',
        en: 'AI videos for elegant and professional communication in the Ticino market.',
        fr: 'Vidéos IA pour une communication élégante et professionnelle sur le marché tessinois.'
      },
      benefits: ['Produzione rapida', 'Multilingue', 'ROI misurabile']
    },
    'webdesign': {
      name: { it: 'Web Design', de: 'Webdesign', en: 'Web Design', fr: 'Conception Web' },
      description: {
        it: 'Siti web eleganti che riflettono lo stile di vita sofisticato del Ticino.',
        de: 'Elegante Websites, die den gehobenen Lebensstil des Tessins widerspiegeln.',
        en: 'Elegant websites that reflect the sophisticated lifestyle of Ticino.',
        fr: 'Sites web élégants qui reflètent le style de vie sophistiqué du Tessin.'
      },
      benefits: ['Design italiano', 'Ottimizzazione SEO', 'Responsive']
    },
    'seo': {
      name: { it: 'SEO', de: 'SEO', en: 'SEO', fr: 'Référencement' },
      description: {
        it: 'Posizionamento strategico per raggiungere clienti locali e internazionali.',
        de: 'Strategische Positionierung für lokale und internationale Kunden.',
        en: 'Strategic positioning to reach local and international customers.',
        fr: 'Positionnement stratégique pour atteindre les clients locaux et internationaux.'
      },
      benefits: ['SEO locale', 'Multilingue', 'Analytics']
    },
    'google-business-profile': {
      name: { it: 'Google Business Profile', de: 'Google Business Profile', en: 'Google Business Profile', fr: 'Profil Google Business' },
      description: {
        it: 'Gestione professionale del vostro profilo per attirare clienti locali.',
        de: 'Professionelle Verwaltung Ihres Profils zur Gewinnung lokaler Kunden.',
        en: 'Professional management of your profile to attract local customers.',
        fr: 'Gestion professionnelle de votre profil pour attirer les clients locaux.'
      },
      benefits: ['Visibilità locale', 'Gestione recensioni', 'Aggiornamenti regolari']
    }
  };

  const currentService = serviceContent[service] || serviceContent['webdesign'];
  const currentCity = cityContent[city] || cityContent['lugano'];

  useEffect(() => {
    const pageTitle = `${currentService.name[currentLanguage]} ${currentCity.name} | Future Level`;
    const pageDescription = currentService.description[currentLanguage] || currentService.description.it;
    const canonicalUrl = `https://futurelevel.ch${location.pathname}`;

    // Update meta tags
    updateMetaTags({
      title: pageTitle,
      description: pageDescription,
      canonicalUrl: canonicalUrl,
      language: currentLanguage
    });

    // Generate hreflang links - simplified for now
    // const hreflangLinks = generateHreflangLinks(location.pathname);

  }, [currentLanguage, service, city, location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <MapPin className="w-4 h-4 mr-2" />
                {currentCity.name}, {currentCity.region}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentService.name[currentLanguage]} {currentCity.name}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentService.description[currentLanguage]}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'it' ? 'Consulenza gratuita' : 
                   currentLanguage === 'de' ? 'Kostenlose Beratung' :
                   currentLanguage === 'fr' ? 'Consultation gratuite' : 'Free Consultation'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'it' ? 'Casi di studio' : 
                   currentLanguage === 'de' ? 'Fallstudien' :
                   currentLanguage === 'fr' ? 'Études de cas' : 'Case Studies'}
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">150+</div>
                  <div className="text-sm text-muted-foreground">Clienti Ticino</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">4.9★</div>
                  <div className="text-sm text-muted-foreground">Recensioni</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">300%</div>
                  <div className="text-sm text-muted-foreground">ROI Medio</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">7 giorni</div>
                  <div className="text-sm text-muted-foreground">Consegna</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Local Proof Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-6">
                    {currentLanguage === 'it' ? 'Successo nel Ticino' : 
                     currentLanguage === 'de' ? 'Erfolg im Tessin' :
                     currentLanguage === 'fr' ? 'Succès au Tessin' : 'Success in Ticino'}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    {currentCity.localProof[currentLanguage]}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {currentCity.industries.map((industry: string, index: number) => (
                      <Badge key={index} variant="secondary" className="justify-center py-2">
                        {industry}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-6 text-center">
                    <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-500">+280%</div>
                    <div className="text-sm text-muted-foreground">Crescita Media</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-500">150+</div>
                    <div className="text-sm text-muted-foreground">Clienti Attivi</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-yellow-500">4.9/5</div>
                    <div className="text-sm text-muted-foreground">Soddisfazione</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Globe className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-purple-500">24h</div>
                    <div className="text-sm text-muted-foreground">Supporto</div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ticino Special Offer */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-8">
                {currentLanguage === 'it' ? 'Offerta Speciale Ticino' : 
                 currentLanguage === 'de' ? 'Tessin Spezialangebot' :
                 currentLanguage === 'fr' ? 'Offre Spéciale Tessin' : 'Ticino Special Offer'}
              </h2>
              <Card className="p-8 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
                <div className="text-xl font-semibold mb-4">
                  {currentCity.specialOffer[currentLanguage]}
                </div>
                <ul className="text-left space-y-2 mb-6 max-w-2xl mx-auto">
                  {currentService.benefits.map((benefit: string, index: number) => (
                    <li key={index} className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90">
                  {currentLanguage === 'it' ? 'Richiedi preventivo' : 
                   currentLanguage === 'de' ? 'Angebot anfordern' :
                   currentLanguage === 'fr' ? 'Demander un devis' : 'Request Quote'}
                </Button>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'it' ? 'Domande Frequenti' : 
                 currentLanguage === 'de' ? 'Häufige Fragen' :
                 currentLanguage === 'fr' ? 'Questions Fréquentes' : 'Frequently Asked Questions'}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    {currentLanguage === 'it' ? `Quanto costa ${currentService.name[currentLanguage]} a Lugano?` : 
                     currentLanguage === 'de' ? `Was kostet ${currentService.name[currentLanguage]} in Lugano?` :
                     currentLanguage === 'fr' ? `Combien coûte ${currentService.name[currentLanguage]} à Lugano?` : 
                     `How much does ${currentService.name[currentLanguage]} cost in Lugano?`}
                  </h3>
                  <p className="text-muted-foreground">
                    {currentLanguage === 'it' ? 'I nostri prezzi sono competitivi e trasparenti. Offriamo pacchetti su misura per le aziende ticinesi a partire da CHF 2,500.' : 
                     currentLanguage === 'de' ? 'Unsere Preise sind wettbewerbsfähig und transparent. Wir bieten massgeschneiderte Pakete für Tessiner Unternehmen ab CHF 2,500.' :
                     currentLanguage === 'fr' ? 'Nos prix sont compétitifs et transparents. Nous proposons des forfaits sur mesure pour les entreprises tessinoises à partir de CHF 2,500.' : 
                     'Our prices are competitive and transparent. We offer tailored packages for Ticino businesses starting from CHF 2,500.'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    {currentLanguage === 'it' ? 'Quanto tempo richiede il progetto?' : 
                     currentLanguage === 'de' ? 'Wie lange dauert das Projekt?' :
                     currentLanguage === 'fr' ? 'Combien de temps prend le projet?' : 'How long does the project take?'}
                  </h3>
                  <p className="text-muted-foreground">
                    {currentLanguage === 'it' ? 'Generalmente 2-4 settimane, a seconda della complessità. Offriamo consegne express per progetti urgenti.' : 
                     currentLanguage === 'de' ? 'In der Regel 2-4 Wochen, je nach Komplexität. Wir bieten Express-Lieferung für dringende Projekte.' :
                     currentLanguage === 'fr' ? 'Généralement 2-4 semaines, selon la complexité. Nous offrons des livraisons express pour les projets urgents.' : 
                     'Generally 2-4 weeks, depending on complexity. We offer express delivery for urgent projects.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'it' ? 'Pronti a crescere nel Ticino?' : 
               currentLanguage === 'de' ? 'Bereit, im Tessin zu wachsen?' :
               currentLanguage === 'fr' ? 'Prêt à croître au Tessin?' : 'Ready to grow in Ticino?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'it' ? 'Contattateci oggi per una consulenza personalizzata per il vostro business ticinese.' : 
               currentLanguage === 'de' ? 'Kontaktieren Sie uns heute für eine personalisierte Beratung für Ihr Tessiner Unternehmen.' :
               currentLanguage === 'fr' ? 'Contactez-nous aujourd\'hui pour une consultation personnalisée pour votre entreprise tessinoise.' : 
               'Contact us today for a personalized consultation for your Ticino business.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'it' ? 'Inizia ora' : 
                 currentLanguage === 'de' ? 'Jetzt starten' :
                 currentLanguage === 'fr' ? 'Commencer maintenant' : 'Start Now'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ItalianSwissCityServicePage;